/**
 * @fileoverview Tooltip Auditável - Componente para rastreabilidade completa
 * 
 * @module TooltipAuditavel
 * @description
 * Tooltip interativo que mostra:
 * - 📐 Fórmula de cálculo
 * - 📊 Fonte dos dados (planilha + aba + linha/célula)
 * - 🧮 Breakdown detalhado dos componentes
 * - ⚠️ Premissas e limitações (opcional)
 * 
 * @features
 * - Design padronizado para auditabilidade
 * - Suporte a fórmulas matemáticas (KaTeX)
 * - Links diretos para células do Excel
 * - Validação visual (success/warning/error)
 * 
 * @example
 * ```tsx
 * <TooltipAuditavel
 *   titulo="Lucro Líquido 2023"
 *   formula="Resultado antes IR - (CSLL + IRPJ)"
 *   fonte={{
 *     planilha: "03_3_DRE_Setima.xlsx",
 *     aba: "DRE_Anual",
 *     linha: "L25"
 *   }}
 *   breakdown={[
 *     { label: "Resultado antes IR", valor: 71373 },
 *     { label: "(-) CSLL + IRPJ", valor: -606519 },
 *     { label: "= Lucro Líquido", valor: -535146, destaque: true }
 *   ]}
 * >
 *   <Typography>-R$ 535.146</Typography>
 * </TooltipAuditavel>
 * ```
 */

import React from 'react';
import {
  Tooltip,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  Info,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { FileText, Info as InfoIcon, AlertTriangle, MapPin } from 'lucide-react';
import { Description } from '../ui';

/**
 * Tipos
 */
interface FonteDados {
  planilha: string;
  aba: string;
  linha?: string;
  celula?: string;
  observacao?: string;
}

interface ItemBreakdown {
  label: string;
  valor?: number | string;
  descricao?: string;
  destaque?: boolean;
  tipo?: 'positivo' | 'negativo' | 'neutro' | 'titulo' | 'separador';
}

interface TooltipAuditavelProps {
  /** Título do tooltip (métrica sendo explicada) */
  titulo: string;
  
  /** Fórmula de cálculo (pode incluir LaTeX se necessário) */
  formula?: string;
  
  /** Fonte dos dados no Excel */
  fonte?: FonteDados;
  
  /** Breakdown detalhado do cálculo */
  breakdown?: ItemBreakdown[];
  
  /** Premissas ou observações importantes */
  premissas?: string[];
  
  /** Status de validação (opcional) */
  status?: 'success' | 'warning' | 'error';
  
  /** Elemento que receberá o tooltip */
  children: React.ReactElement;
  
  /** Posicionamento do tooltip */
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * Componente Principal
 */
export const TooltipAuditavel: React.FC<TooltipAuditavelProps> = ({
  titulo,
  formula,
  fonte,
  breakdown,
  premissas,
  status,
  children,
  placement = 'top',
}) => {
  // Ícone de status
  const StatusIcon = status === 'success' 
    ? CheckCircle 
    : status === 'warning' 
    ? Warning 
    : status === 'error'
    ? ErrorIcon
    : Info;

  const statusColor = status === 'success'
    ? 'success.main'
    : status === 'warning'
    ? 'warning.main'
    : status === 'error'
    ? 'error.main'
    : 'info.main';

  // Formatar valor
  const formatarValor = (valor: number | string): string => {
    if (typeof valor === 'string') return valor;
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(valor);
  };

  // Conteúdo do tooltip
  const tooltipContent = (
    <Box sx={{ maxWidth: 450, p: 2 }}>
      {/* Cabeçalho - Mais compacto */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <StatusIcon sx={{ fontSize: 18, color: statusColor }} />
        <Typography 
          variant="subtitle2" 
          fontWeight="600"
          sx={{ 
            color: '#F8F8F2 !important',
            fontSize: '0.875rem',
            letterSpacing: '0.3px'
          }}
        >
          {titulo}
        </Typography>
      </Box>

      {/* Fórmula - Destaque visual melhorado */}
      {formula && (
        <Box mb={2}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              bgcolor: 'rgba(59, 130, 246, 0.08)', // Azul suave
              border: '1px solid rgba(59, 130, 246, 0.2)',
              p: 1.5,
              borderRadius: 1,
              fontSize: '0.8125rem',
              color: '#E0E7FF',
              fontWeight: 500,
              lineHeight: 1.6,
              whiteSpace: 'pre-line',
            }}
          >
            {formula}
          </Typography>
        </Box>
      )}

      {/* Fonte */}
      {fonte && (
        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={0.5} mb={1}>
            <FileText size={16} color="#A8ABB0" />
            <Typography
              variant="caption"
              sx={{
                color: '#F8F8F2 !important',
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Fonte
            </Typography>
          </Box>
          <Description sx={{ color: '#E5E7EB !important', display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: '4px' }}>
            <FileText size={14} />
            {fonte.planilha}
          </Description>
          <Description sx={{ color: '#E5E7EB !important', display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: '4px' }}>
            <FileText size={14} />
            Aba: <strong>{fonte.aba}</strong>
          </Description>
          {(fonte.linha || fonte.celula) && (
            <Description sx={{ color: '#E5E7EB !important', display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: '4px' }}>
              <MapPin size={14} />
              {fonte.celula || `Linha ${fonte.linha}`}
            </Description>
          )}
          {fonte.observacao && (
            <Description sx={{ marginTop: '4px', fontStyle: 'italic', color: '#D1D5DB !important', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <InfoIcon size={14} />
              {fonte.observacao}
            </Description>
          )}
        </Box>
      )}

      {/* Breakdown - Design melhorado */}
      {breakdown && breakdown.length > 0 && (
        <Box>
          <Divider sx={{ my: 2, borderColor: '#374151' }} />
          <Table size="small" sx={{ 
            bgcolor: 'transparent !important',
            '& td': { 
              py: 0.75, 
              px: 1, 
              border: 'none',
              fontSize: '0.8125rem',
              bgcolor: 'transparent !important'
            },
            '& tr': {
              bgcolor: 'transparent !important'
            }
          }}>
            <TableBody sx={{ bgcolor: 'transparent !important' }}>
              {breakdown.map((item, index) => {
                // Separador (linha vazia com divider)
                if (item.tipo === 'separador') {
                  return (
                    <TableRow key={index} sx={{ bgcolor: 'transparent !important' }}>
                      <TableCell colSpan={2} sx={{ py: 0.5, px: 0, bgcolor: 'transparent !important' }}>
                        <Divider sx={{ borderColor: '#374151', opacity: 0.5 }} />
                      </TableCell>
                    </TableRow>
                  );
                }

                // Título de seção
                if (item.tipo === 'titulo') {
                  return (
                    <TableRow key={index} sx={{ bgcolor: 'transparent !important' }}>
                      <TableCell colSpan={2} sx={{ pt: index > 0 ? 1.5 : 0, pb: 0.75, px: 0, bgcolor: 'transparent !important' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.6875rem',
                            color: '#D1D5DB !important',
                            textTransform: 'uppercase',
                            letterSpacing: '0.8px',
                          }}
                        >
                          {item.label}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }

                // Item normal
                return (
                  <TableRow
                    key={index}
                    sx={{
                      bgcolor: item.destaque
                        ? 'rgba(59, 130, 246, 0.15) !important'
                        : 'transparent !important',
                      borderRadius: item.destaque ? 1 : 0,
                    }}
                  >
                    <TableCell sx={{ width: '50%', bgcolor: 'transparent !important' }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            marginBottom: 0,
                            fontWeight: item.destaque ? 600 : 400,
                            color: item.destaque ? '#FFFFFF !important' : '#E5E7EB !important',
                            fontSize: item.destaque ? '0.8125rem' : '0.78125rem',
                          }}
                        >
                          {item.label}
                        </Typography>
                        {item.descricao && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.6875rem',
                              color: '#9CA3AF !important',
                              fontStyle: 'italic',
                              marginTop: '2px',
                              display: 'block',
                            }}
                          >
                            {item.descricao}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ width: '50%', bgcolor: 'transparent !important' }}>
                      {item.valor !== undefined && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: item.destaque ? 700 : 500,
                            fontSize: item.destaque ? '0.875rem' : '0.8125rem',
                            fontFamily: item.destaque ? 'monospace' : 'inherit',
                            color: item.tipo === 'positivo' 
                              ? '#34D399 !important'
                              : item.tipo === 'negativo'
                              ? '#F87171 !important'
                              : '#FFFFFF !important',
                          }}
                        >
                          {formatarValor(item.valor)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Premissas - Design melhorado */}
      {premissas && premissas.length > 0 && (
        <Box>
          <Divider sx={{ my: 2, borderColor: '#374151' }} />
          <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
            <AlertTriangle size={14} color="#9CA3AF" />
            <Typography
              variant="caption"
              sx={{
                color: '#9CA3AF !important',
                fontWeight: 600,
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
              }}
            >
              Premissas
            </Typography>
          </Box>
          <Box component="ul" sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
            {premissas.map((premissa, index) => (
              <Box key={index} component="li" sx={{ marginBottom: '6px' }}>
                <Typography
                  variant="body2"
                  sx={{
                    marginBottom: 0,
                    color: '#E5E7EB !important',
                    fontSize: '0.78125rem',
                    lineHeight: 1.5,
                  }}
                >
                  {premissa}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement={placement}
      enterDelay={200}
      leaveDelay={150}
      disableInteractive={true}
      componentsProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ],
        },
        tooltip: {
          sx: {
            bgcolor: '#1E2329 !important',
            color: '#F8F8F2 !important',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            border: '1px solid #374151',
            borderRadius: '8px',
            maxWidth: 450,
            p: 0,
            '& .MuiTable-root': {
              '& .MuiTableRow-root': {
                bgcolor: 'transparent !important',
              },
              '& .MuiTableCell-root': {
                bgcolor: 'transparent !important',
                borderColor: 'transparent !important',
              },
            },
          },
        },
        arrow: {
          sx: {
            color: '#1E2329 !important',
            '&::before': {
              border: '1px solid #374151',
            },
          },
        },
      }}
    >
      <Box
        component="span"
        sx={{
          cursor: 'help',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '3px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

/**
 * Variante simplificada para métricas rápidas
 */
export const TooltipMetrica: React.FC<{
  metrica: string;
  valor: number;
  formula: string;
  children: React.ReactElement;
}> = ({ metrica, valor, formula, children }) => {
  return (
    <TooltipAuditavel
      titulo={metrica}
      formula={formula}
      breakdown={[
        {
          label: metrica,
          valor,
          destaque: true,
        },
      ]}
    >
      {children}
    </TooltipAuditavel>
  );
};

export default TooltipAuditavel;
